import { useCallback, useEffect, useState } from "react";
import { Download, FileText, LogOut, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { StaffOnly } from "@/pages/Admin";
import { CompcardDialog } from "@/components/admin/CompcardDialog";

export default function Compcard() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/compcards").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);
  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  if (user === null) return <main className="min-h-screen bg-white" />;
  if (!user) return <AdminLogin />;
  if (user.role !== "admin") return <StaffOnly user={user} logout={logout} />;

  return (
    <main data-testid="compcard-page" className="min-h-screen bg-white px-5 pb-24 pt-28 text-[#1c1418] md:px-10">
      <div className="mx-auto max-w-[1400px]">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-[#1c1418] pb-4">
          <h1 className="font-display text-6xl uppercase leading-none md:text-8xl">Compcard.</h1>
          <div className="flex items-center gap-6 font-mono text-xs uppercase tracking-[0.2em]">
            <Link to="/admin" data-testid="compcard-admin-link" className="hover:underline">Pannello</Link>
            <button onClick={logout} className="flex items-center gap-2 hover:underline"><LogOut className="h-4 w-4" /> Esci</button>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <a href={`${API}/admin/composites.zip`} data-testid="compcard-download-all"
            onClick={() => toast("Preparo le compcard di tutte le modelle… il download parte tra qualche secondo")}
            className="flex items-center gap-2 bg-[#1c1418] px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white">
            <Download className="h-4 w-4 text-[#e9458f]" /> Scarica tutte le compcard (ZIP · {items.length} PDF)
          </a>
          <p className="max-w-md text-sm text-[#1c1418]/70">Ogni PDF ha 2 pagine (copertina + composite). Tocca la matita per cambiare le foto di una compcard.</p>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
          {items.map((m) => (
            <div key={m.id} data-testid={`compcard-${m.slug}`}>
              <div className="relative aspect-[3/4] overflow-hidden bg-[#f4f4f4]">
                {m.cover && <img src={m.cover} alt={m.name} loading="lazy" className="h-full w-full object-cover" />}
                {m.custom && <span className="absolute left-2 top-2 bg-white px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em]">Personalizzata</span>}
                <div className="absolute inset-x-0 bottom-0 flex justify-end gap-1 p-2">
                  <a href={`${API}/models/${m.slug}/composite.pdf`} data-testid={`compcard-pdf-${m.slug}`} title="Scarica PDF" className="grid h-9 w-9 place-items-center bg-white"><FileText className="h-4 w-4 text-[#e9458f]" /></a>
                  <button onClick={() => setEditing(m)} data-testid={`compcard-edit-${m.slug}`} title="Cambia foto" className="grid h-9 w-9 place-items-center bg-[#1c1418] text-white"><Pencil className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-1 grid grid-cols-4 gap-1">
                {m.grid.map((g) => <img key={g} src={g} alt="" loading="lazy" className="aspect-[3/4] w-full object-cover" />)}
              </div>
              <p className="mt-2 font-display text-xl uppercase leading-none">{m.name}</p>
            </div>
          ))}
        </div>
      </div>
      {editing && <CompcardDialog model={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </main>
  );
}
